import { LitElement, css, html } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { ApiService } from '../services/api-service.js';
import { teknikTokens, teknikButtons } from '../styles/teknik-tokens.js';
import '../components/header.js';
import '../components/onboarding-checklist.js';

interface PlatformStats {
  servers: number;
  users: number;
  minecraftServers: number;
  lunaBots: number;
  customCommands: number;
  totalMessages: number;
  economyAccounts: number;
  commandCount: number;
  actionGifs: number;
  uptime: number;
  memoryUsage?: number;
}

type LiveState = 'loading' | 'online' | 'offline' | 'unknown';

interface SignalCtx {
  stats: PlatformStats | null;
}

interface Project {
  id: string;
  name: string;
  /** One line. What it is, in the words someone would use to ask for it. */
  tagline: string;
  /** Featured projects get a second paragraph; the rest don't earn one. */
  body?: string;
  href: string;
  linkLabel: string;
  external?: boolean;
  stack: string[];
  featured?: boolean;
  /** Live figures, pulled from the real APIs. Returns null when we don't have them. */
  meta?: (ctx: SignalCtx) => string | null;
  /** Which liveness feed drives this row's dot. Rows without one show no dot,
      rather than a decorative green light that means nothing. */
  live?: 'luna';
}

/**
 * Three tiers, because the page was answering two questions at once: what is
 * this person's headline work, and what can I actually open right now. Luna is
 * the first; HERE is the second; ELSEWHERE is everything that lives on another
 * domain and only needs a line and a link.
 */
const FLAGSHIP: Project = {
  id: 'luna',
  name: 'Luna',
  tagline: 'A Discord bot.',
  body: 'Economy, music, moderation, leveling, tickets, AI chat, and a few hundred games. Set it up from the dashboard or from chat.',
  href: 'https://lunako.app',
  linkLabel: 'lunako.app',
  external: true,
  stack: ['Node', 'discord.js', 'Postgres', 'Canvas'],
  live: 'luna',
  meta: ({ stats }) =>
    stats ? `${stats.servers.toLocaleString()} servers · ${stats.users.toLocaleString()} members` : null,
};

/** Things a visitor can open on this site. */
const HERE: Project[] = [
  {
    id: 'cloud',
    name: 'Cloud',
    tagline: '50 GB of storage, on the web and your phone.',
    href: '/cloud',
    linkLabel: 'Open',
    stack: ['Express', 'Expo'],
  },
  {
    id: 'messages',
    name: 'Messages',
    tagline: 'DMs, friends and servers.',
    href: '/messages',
    linkLabel: 'Open',
    stack: ['Socket.IO', 'Expo'],
  },
  {
    id: 'games',
    name: 'Games',
    tagline: 'The public Minecraft server.',
    href: '/games',
    linkLabel: 'Open',
    stack: ['Paper', 'BlueMap'],
  },
  {
    id: 'books',
    name: 'Books',
    tagline: 'Write on the web, read in Discord.',
    href: '/books',
    linkLabel: 'Open',
    stack: ['Postgres', 'Canvas'],
  },
];

/** Built here, lives on its own domain. A line and a link is enough. */
const ELSEWHERE: Project[] = [
  {
    id: 'sumiko',
    name: 'Sumiko',
    tagline: 'Luna, rebuilt in TypeScript.',
    href: 'https://sumiko.app',
    linkLabel: 'sumiko.app',
    external: true,
    stack: [],
  },
  {
    id: 'kohosting',
    name: 'KO Hosting',
    tagline: 'Game server hosting.',
    href: 'https://kohosting.app',
    linkLabel: 'kohosting.app',
    external: true,
    stack: [],
  },
  {
    id: 'plugins',
    name: 'Plugin platform',
    tagline: 'One plugin, both bots.',
    href: 'https://lunako.app/developers',
    linkLabel: 'Read the docs',
    external: true,
    stack: [],
  },
];



@customElement('app-home')
export class AppHome extends LitElement {
  @property() message = 'Teknik';
  @property({ type: String }) currentPage = 'Home';
  @state() userInfo: any = null;
  @state() stats: PlatformStats | null = null;
  @state() statsLoading = true;
  @state() statsError = false;
  @state() lunaState: LiveState = 'loading';

  static styles = [
    teknikTokens,
    teknikButtons,
    css`
      :host {
        display: block;
        width: 100%;
        font-family: var(--font-family);
        color: var(--tk-text);
      }

      @keyframes tk-rise {
        from { opacity: 0; transform: translateY(10px); }
        to   { opacity: 1; transform: none; }
      }
      @keyframes tk-pulse {
        0%, 100% { opacity: 0.35; }
        50%      { opacity: 0.9; }
      }

      main {
        background: var(--tk-bg);
        padding: calc(48px + 3.5rem) 0 7rem;
        min-height: 100vh;
      }

      .wrap {
        width: 100%;
        max-width: var(--tk-max);
        margin: 0 auto;
        padding: 0 24px;
        box-sizing: border-box;
      }

      /* ── Hero ─────────────────────────────────────────────────────── */
      .hero {
        animation: tk-rise 0.5s ease both;
      }

      .hero h1 {
        font-family: var(--font-display);
        font-weight: 700;
        font-size: clamp(2.5rem, 6.5vw, 4rem);
        line-height: 1.02;
        letter-spacing: -0.02em;
        margin: 0 0 1.1rem;
        max-width: 15ch;
        text-wrap: balance;
      }

      /* ── Running now ──────────────────────────────────────────────── */
      .running {
        margin-top: 3rem;
        border: 1px solid var(--tk-border);
        border-radius: var(--tk-r-card);
        background: var(--tk-panel);
        animation: tk-rise 0.5s ease 0.08s both;
      }

      .running-head {
        display: flex;
        align-items: center;
        gap: 0.6rem;
        padding: 0.85rem 1.15rem;
        border-bottom: 1px solid var(--tk-border);
        font-family: var(--font-mono);
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--tk-dim);
      }

      .tiles {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
      }

      .tile {
        padding: 1.2rem 1.15rem 1.3rem;
        border-right: 1px solid var(--tk-border);
        min-width: 0;
      }
      /* Six tiles in three columns: no right border on the last of each row, no
         bottom border on the final row. */
      .tile:nth-child(3n) { border-right: 0; }
      .tile:nth-child(-n + 3) { border-bottom: 1px solid var(--tk-border); }

      .tile-n {
        font-family: var(--font-mono);
        font-size: clamp(1.35rem, 3.2vw, 1.75rem);
        font-weight: 500;
        color: var(--tk-text);
        font-variant-numeric: tabular-nums;
        line-height: 1.1;
        letter-spacing: -0.01em;
      }

      .tile-l {
        margin-top: 0.4rem;
        font-size: 0.8125rem;
        color: var(--tk-dim);
      }

      .tile-skel {
        height: 1.4rem;
        width: 60%;
        border-radius: 4px;
        background: var(--tk-panel-2);
        animation: tk-pulse 1.4s ease-in-out infinite;
      }

      .running-note {
        padding: 1.2rem 1.15rem;
        font-size: 0.875rem;
        color: var(--tk-dim);
      }

      /* ── Section rules ────────────────────────────────────────────── */
      .rule {
        display: flex;
        align-items: center;
        gap: 1rem;
        margin: 4.5rem 0 1.5rem;
      }
      .rule span {
        font-family: var(--font-mono);
        font-size: 11px;
        font-weight: 500;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--tk-dim);
        white-space: nowrap;
      }
      .rule::after {
        content: '';
        flex: 1;
        height: 1px;
        background: var(--tk-border);
      }

      /* Shared by the flagship's status light and the Running-now header. */
      .dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        flex-shrink: 0;
        background: var(--tk-dim);
      }
      .dot.online  { background: var(--tk-ok); box-shadow: 0 0 0 3px rgba(63,185,132,.14); }
      .dot.offline { background: var(--tk-down); }
      .dot.loading { animation: tk-pulse 1.4s ease-in-out infinite; }

      /* ── Flagship ─────────────────────────────────────────────────── */
      .flagship {
        display: block;
        margin-top: 3rem;
        padding: 2rem 1.75rem 1.6rem;
        border: 1px solid var(--tk-border-strong);
        border-radius: var(--tk-r-card);
        background: var(--tk-panel);
        text-decoration: none;
        color: inherit;
        transition: border-color 0.18s ease, background 0.18s ease;
        animation: tk-rise 0.45s ease both;
      }
      .flagship:hover { border-color: var(--tk-accent-line); background: var(--tk-panel-2); }

      .flagship-head {
        display: flex;
        align-items: center;
        gap: 0.9rem;
        flex-wrap: wrap;
      }
      .flagship-name {
        margin: 0;
        font-family: var(--font-display);
        font-size: clamp(2rem, 4.5vw, 2.75rem);
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--tk-text);
      }
      .flagship-live {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        font-family: var(--font-mono);
        font-size: 11px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--tk-dim);
      }
      .flagship-tagline {
        margin: 0.5rem 0 0;
        font-size: 1.15rem;
        color: var(--tk-muted);
      }
      .flagship-body {
        margin: 0.9rem 0 0;
        font-size: 0.9375rem;
        line-height: 1.65;
        color: var(--tk-dim);
        max-width: 60ch;
      }
      .flagship-meta {
        margin-top: 1.1rem;
        font-family: var(--font-mono);
        font-size: 13px;
        font-variant-numeric: tabular-nums;
        color: var(--tk-text);
      }

      .row-foot {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 0.75rem;
        margin-top: 1.3rem;
        min-width: 0;
      }
      .stack { display: flex; flex-wrap: wrap; gap: 6px; min-width: 0; }
      .stack em {
        font-family: var(--font-mono);
        font-size: 11px;
        font-style: normal;
        color: var(--tk-dim);
        border: 1px solid var(--tk-border);
        border-radius: 5px;
        padding: 3px 7px;
        white-space: nowrap;
      }
      .go { font-family: var(--font-mono); font-size: 12px; color: var(--tk-accent); white-space: nowrap; }
      .flagship:hover .go { color: var(--tk-accent-hover); }

      /* ── On this site ─────────────────────────────────────────────── */
      .cards {
        display: grid;
        /* Two columns, not auto-fit: four items in a three-wide grid strands one
           on its own row. */
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 10px;
      }
      .card {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: 1.15rem 1.25rem;
        border: 1px solid var(--tk-border);
        border-radius: var(--tk-r-card);
        background: var(--tk-panel);
        text-decoration: none;
        color: inherit;
        min-width: 0;
        transition: border-color 0.18s ease, background 0.18s ease;
        animation: tk-rise 0.45s ease both;
      }
      .card:hover { border-color: var(--tk-border-strong); background: var(--tk-panel-2); }
      .card-main { flex: 1; min-width: 0; }
      .card-name {
        margin: 0;
        font-family: var(--font-display);
        font-size: 1.15rem;
        font-weight: 600;
        letter-spacing: -0.01em;
        color: var(--tk-text);
      }
      .card-tagline {
        margin: 0.25rem 0 0;
        font-size: 0.875rem;
        line-height: 1.5;
        color: var(--tk-dim);
      }
      /* These are usable right now, so they get a button rather than an arrow. */
      .card-open {
        flex-shrink: 0;
        padding: 0.4rem 0.85rem;
        border: 1px solid var(--tk-border-strong);
        border-radius: var(--tk-r-control);
        font-size: 12px;
        color: var(--tk-muted);
        transition: border-color 0.15s ease, color 0.15s ease;
      }
      .card:hover .card-open { border-color: var(--tk-accent-line); color: var(--tk-accent); }

      /* ── Elsewhere ────────────────────────────────────────────────── */
      .aways { display: flex; flex-direction: column; }
      .away {
        display: flex;
        align-items: baseline;
        gap: 0.9rem;
        padding: 0.85rem 0.25rem;
        border-bottom: 1px solid var(--tk-border);
        text-decoration: none;
        color: inherit;
        min-width: 0;
      }
      .away:last-child { border-bottom: 0; }
      .away-name { font-size: 0.9375rem; font-weight: 600; color: var(--tk-text); white-space: nowrap; }
      .away-tagline { flex: 1; min-width: 0; font-size: 0.875rem; color: var(--tk-dim); }
      .away-host { font-family: var(--font-mono); font-size: 11px; color: var(--tk-dim); white-space: nowrap; }
      .away:hover .away-host { color: var(--tk-accent); }

      /* ── Foot ─────────────────────────────────────────────────────── */
      .foot {
        margin-top: 4.5rem;
        padding-top: 1.6rem;
        border-top: 1px solid var(--tk-border);
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem 1.5rem;
        align-items: center;
      }
      .foot a {
        font-size: 0.875rem;
        color: var(--tk-muted);
        text-decoration: none;
      }
      .foot a:hover { color: var(--tk-accent); }
      .foot .spacer { flex: 1; }
      .foot .fine {
        font-family: var(--font-mono);
        font-size: 11px;
        color: var(--tk-dim);
      }

      /* ── Narrow ───────────────────────────────────────────────────── */
      @media (max-width: 760px) {
        main { padding-top: calc(48px + 2.5rem); }
        .wrap { padding: 0 18px; }
        .tiles { grid-template-columns: repeat(2, 1fr); }
        .tile { border-right: 1px solid var(--tk-border); border-bottom: 1px solid var(--tk-border); }
        .tile:nth-child(2n) { border-right: 0; }
        .tile:nth-last-child(-n + 2) { border-bottom: 0; }
        .flagship { padding: 1.5rem 1.25rem 1.25rem; }
        .cards { grid-template-columns: minmax(0, 1fr); }
        .card { padding: 1rem 1.1rem; }
        .away { flex-wrap: wrap; gap: 0.35rem 0.9rem; }
        .away-tagline { flex-basis: 100%; }
        .rule { margin: 3.25rem 0 1.25rem; }
      }

      @media (prefers-reduced-motion: reduce) {
        .hero, .running, .flagship, .card { animation: none; }
        .dot.loading, .tile-skel { animation: none; }
      }
    `,
  ];

  async firstUpdated() {
    const header = this.renderRoot.querySelector('app-header');
    if (header) header.setAttribute('pageTitle', this.currentPage);
    this.loadUserInfo();
    this.loadStats();
    this.loadLunaState();
  }

  async loadUserInfo() {
    try {
      const authStatus = await ApiService.getAuthStatus();
      if (authStatus.authenticated) this.userInfo = authStatus.user;
    } catch (error) {
      console.error('Error loading user info:', error);
    }
  }

  async loadStats() {
    this.statsLoading = true;
    this.statsError = false;
    try {
      const data = await ApiService.getStats();
      this.stats = {
        servers: data.servers ?? 0,
        users: data.users ?? 0,
        minecraftServers: data.minecraftServers ?? 0,
        lunaBots: data.lunaBots ?? 0,
        customCommands: data.customCommands ?? 0,
        totalMessages: data.totalMessages ?? 0,
        economyAccounts: data.economyAccounts ?? 0,
        commandCount: data.commandCount ?? 0,
        actionGifs: data.actionGifs ?? 0,
        uptime: data.uptime ?? 0,
        memoryUsage: data.memoryUsage,
      };
    } catch (error) {
      console.error('Error loading stats:', error);
      this.statsError = true;
      this.stats = null;
    } finally {
      this.statsLoading = false;
    }
  }

  // A dot that is green because the fetch failed is worse than no dot at all,
  // so an unreachable status API lands on 'unknown', not 'offline'.
  async loadLunaState() {
    try {
      const res = await fetch('/api/bot-status/luna', { credentials: 'include' });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      this.lunaState = data?.status === 'online' ? 'online' : data?.status === 'offline' ? 'offline' : 'unknown';
    } catch {
      this.lunaState = 'unknown';
    }
  }

  getUserAvatarUrl(user: any) {
    if (user?.avatar) return `https://cdn.discordapp.com/avatars/${user.id}/${user.avatar}.png?size=64`;
    if (user?.id) return `https://cdn.discordapp.com/embed/avatars/${parseInt(user.discriminator || '0') % 5}.png`;
    return '';
  }

  private liveLabel(state: LiveState) {
    if (state === 'online') return 'online';
    if (state === 'offline') return 'offline';
    if (state === 'loading') return 'checking';
    return 'status unavailable';
  }

  renderRunning() {
    if (this.statsError) {
      return html`<div class="running-note">Stats are unavailable right now.</div>`;
    }

    const s = this.stats;
    // Only figures worth printing. minecraftServers (2), lunaBots (1) and books
    // (6) are real numbers that read as small, and a tile that says 2 is worse
    // than no tile — so they stay off.
    const tiles: Array<[string, string]> = [
      [s ? s.users.toLocaleString() : '', 'members reached'],
      [s ? s.totalMessages.toLocaleString() : '', 'messages handled'],
      [s ? s.servers.toLocaleString() : '', 'Discord servers'],
      [s ? s.economyAccounts.toLocaleString() : '', 'wallets'],
      [s ? s.commandCount.toLocaleString() : '', 'commands'],
      [s ? s.actionGifs.toLocaleString() : '', 'GIFs self-hosted'],
    ];

    return html`
      <div class="tiles">
        ${tiles.map(
          ([value, label]) => html`
            <div class="tile">
              ${this.statsLoading || !s
                ? html`<div class="tile-skel"></div>`
                : html`<div class="tile-n">${value}</div>`}
              <div class="tile-l">${label}</div>
            </div>
          `
        )}
      </div>
    `;
  }

  /** The headline. Gets the size, the live figures and the only status dot. */
  renderFlagship() {
    const p = FLAGSHIP;
    const meta = p.meta ? p.meta({ stats: this.stats }) : null;
    const state = p.live === 'luna' ? this.lunaState : null;

    return html`
      <a class="flagship" href="${p.href}" target="_blank" rel="noopener">
        <div class="flagship-head">
          <h2 class="flagship-name">${p.name}</h2>
          ${state
            ? html`<span class="flagship-live"><span class="dot ${state}"></span>${this.liveLabel(state)}</span>`
            : ''}
        </div>
        <p class="flagship-tagline">${p.tagline}</p>
        ${p.body ? html`<p class="flagship-body">${p.body}</p>` : ''}
        ${meta ? html`<div class="flagship-meta">${meta}</div>` : ''}
        <div class="row-foot">
          <div class="stack">${p.stack.map((t) => html`<em>${t}</em>`)}</div>
          <span class="go">${p.linkLabel} →</span>
        </div>
      </a>
    `;
  }

  /** Openable on this site, so the affordance is a button, not a link arrow. */
  renderHere(p: Project, index: number) {
    return html`
      <a class="card" href="${p.href}" style="animation-delay:${Math.min(index, 6) * 45}ms">
        <div class="card-main">
          <h3 class="card-name">${p.name}</h3>
          <p class="card-tagline">${p.tagline}</p>
        </div>
        <span class="card-open">${p.linkLabel}</span>
      </a>
    `;
  }

  /** One line. It lives on another domain, so the domain is the payload. */
  renderElsewhere(p: Project) {
    return html`
      <a class="away" href="${p.href}" target="_blank" rel="noopener">
        <span class="away-name">${p.name}</span>
        <span class="away-tagline">${p.tagline}</span>
        <span class="away-host">${p.linkLabel} →</span>
      </a>
    `;
  }

  render() {
    return html`
      <app-header
        pageTitle="${this.currentPage}"
        .isLoggedIn=${!!this.userInfo}
        .userData=${{ firstName: this.userInfo?.username || '', lastName: '' }}
        .userAvatar=${this.userInfo ? this.getUserAvatarUrl(this.userInfo) : ''}
      ></app-header>

      <main>
        <div class="wrap">
          <onboarding-checklist></onboarding-checklist>

          <section class="hero">
            <h1>Things I build and run.</h1>
          </section>

          <section class="running" aria-label="Live platform figures">
            <div class="running-head">
              <span class="dot ${this.statsLoading ? 'loading' : this.statsError ? 'offline' : 'online'}"></span>
              Running now
            </div>
            ${this.renderRunning()}
          </section>

          ${this.renderFlagship()}

          <div class="rule"><span>On this site</span></div>
          <section class="cards">
            ${HERE.map((p, i) => this.renderHere(p, i))}
          </section>

          <div class="rule"><span>Elsewhere</span></div>
          <section class="aways">
            ${ELSEWHERE.map((p) => this.renderElsewhere(p))}
          </section>

          <div class="foot">
            <a href="https://kohosting.app" target="_blank" rel="noopener">kohosting.app</a>
            <a href="/store">Store</a>
            <a href="/account">Account</a>
            <a href="https://lunako.app" target="_blank" rel="noopener">lunako.app</a>
            <a href="https://sumiko.app" target="_blank" rel="noopener">sumiko.app</a>
            <span class="spacer"></span>
            <span class="fine">teknik.sh</span>
          </div>
        </div>
      </main>
    `;
  }
}
